import { TextField } from '@quintype/em/components/text-field';
import React from 'react';
import get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import { updateHaveUnsavedChanges, updatePageSettings, updateSessionLimitSettings } from '../../actions/builder-action';
import { ToggleButton } from '../toggle-button';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FieldLabel } from '@quintype/em/components/field-label';
import { ThemeSetter } from '../theme-setter';
import { Separator } from '../../atoms/separator';
import { allCountries } from 'country-telephone-data';
import styles from '../subscription/subscription.module.css';
import Select from '@quintype/em/components/select';
import { updateMeterSettings } from '../../actions/builder-action';
import { Accordion, AccordionSection } from '@quintype/em/components/accordion';
import { IPBState } from '../../utils/interfaces';
import { TextArea } from '@quintype/em/components/text-area';

interface IOptions {
  label: string;
  value: string;
  countryIso: string;
}

interface IDataItems {
  type: string;
  label: string;
  key: string;
  defaultValue?: string | boolean | IOptions;
  placeHolder?: string;
  darkKey?: string;
  options?: IOptions[];
  onChange?: (key: string, value: any) => void;
  value?: any;
}

export const LoginSettings = () => {
  const dispatch = useDispatch();
  const onClickHandler = useSelector((state: IPBState) => {
    return (prevState: { isAccordionActive: any }) => ({ isAccordionActive: !prevState.isAccordionActive });
  });
  const { login = {}, enableDarkModePreview = false, sessionLimit = {} } = useSelector(state =>
    get(state, ['builder', 'config'], {})
  );
  const { pageSetting = {} } = login;
  const updateLogin = (key: string, value: string | boolean) => {
    dispatch(updatePageSettings({ [key]: value }, 'login'));

    switch (key) {
      case 'buttonColor':
        dispatch(updateMeterSettings({ key: 'loginButtonColor', value }));
        break;
      case 'darkButtonColor':
        dispatch(updateMeterSettings({ key: 'loginDarkButtonColor', value }));
        break;
    }

    dispatch(updateHaveUnsavedChanges(true));
  };

  const updateSessionLimitSetting = (key: string, value: string | boolean) => {
    dispatch(updateSessionLimitSettings({ [key]: value }));
    dispatch(updateHaveUnsavedChanges(true));
  };

  const countryCodeOptions = allCountries.map((country: any, index: number) => {
    return {
      label: `${country?.name} (+${country?.dialCode})`,
      value: `+${country?.dialCode}`,
      countryIso: country?.iso2
    };
  });

  const setting = [
    {
      label: 'Authentication Methods',
      type: 'label',
      key: 'authMethods'
    },
    {
      label: 'Mobile & OTP',
      type: 'toggle',
      key: 'mobileOtpAuth',
      onChange: updateLogin,
      value: pageSetting
    },
    {
      label: 'Google',
      type: 'toggle',
      key: 'googleAuth',
      defaultValue: true,
      onChange: updateLogin,
      value: pageSetting
    },
    {
      label: 'Facebook',
      type: 'toggle',
      key: 'facebookAuth',
      defaultValue: true,
      onChange: updateLogin,
      value: pageSetting
    },
    {
      label: 'LinkedIn',
      type: 'toggle',
      key: 'linkedInAuth',
      defaultValue: true,
      onChange: updateLogin,
      value: pageSetting
    },
    {
      label: 'Apple',
      type: 'toggle',
      key: 'appleAuth',
      defaultValue: true,
      onChange: updateLogin,
      value: pageSetting
    },
    {
      label: 'Default Country Code',
      key: 'defaultCountryCode',
      type: 'select',
      options: countryCodeOptions,
      onChange: updateLogin,
      value: pageSetting
    },
    {
      type: 'colorPicker',
      label: 'Button Color',
      key: 'buttonColor',
      darkKey: 'darkButtonColor',
      onChange: updateLogin,
      value: pageSetting
    },
    {
      label: 'Success Messages',
      type: 'label',
      key: 'successLabel'
    },
    {
      label: 'Sign in successful.',
      type: 'textField',
      key: 'loginSuccess',
      placeHolder: '',
      onChange: updateLogin,
      value: pageSetting
    },
    {
      label: 'Sign up successful.',
      type: 'textField',
      key: 'signupSuccess',
      placeHolder: '',
      onChange: updateLogin,
      value: pageSetting
    },
    {
      label: 'Password reset successful.',
      type: 'textField',
      key: 'passwordResetSuccess',
      placeHolder: '',
      onChange: updateLogin,
      value: pageSetting
    },
    {
      label: 'Error Handling',
      type: 'label',
      key: 'errorLabel'
    },
    {
      label: 'Please enter a valid email address.',
      type: 'textField',
      key: 'emailError',
      placeHolder: '',
      onChange: updateLogin,
      value: pageSetting
    },
    {
      label: 'Please enter a valid phone number.',
      type: 'textField',
      key: 'phoneError',
      placeHolder: '',
      onChange: updateLogin,
      value: pageSetting
    },
    {
      label: 'Password entered is invalid.',
      type: 'textField',
      key: 'passwordError',
      placeHolder: '',
      onChange: updateLogin,
      value: pageSetting
    },
    {
      label: 'OTP entered is invalid.',
      type: 'textField',
      key: 'otpError',
      placeHolder: '',
      onChange: updateLogin,
      value: pageSetting
    },
    {
      label: 'Email already exists.',
      type: 'textField',
      key: 'existsError',
      placeHolder: '',
      onChange: updateLogin,
      value: pageSetting
    },
    {
      label: 'Something went wrong, please try after sometime.',
      type: 'textField',
      key: 'genericError',
      placeHolder: '',
      onChange: updateLogin,
      value: pageSetting
    }
  ];

  const basicSessionLimitSetting = [
    {
      label: 'This feature allows you to restrict the number of active sessions per reader account.',
      type: 'description',
      key: ''
    },
    {
      label: 'Session Limiting',
      type: 'toggle',
      key: 'enableSessionLimit',
      defaultValue: false,
      onChange: updateSessionLimitSetting,
      value: sessionLimit
    }
  ];

  const restSessionLimitSetting = [
    {
      label: 'Max sessions per user',
      type: 'textField',
      key: 'maxSessionsPerUser',
      placeHolder: 'Enter a value',
      onChange: updateSessionLimitSetting,
      value: sessionLimit
    },
    {
      label: 'Blocked Session Message',
      type: 'textArea',
      key: 'blockedSessionMessage',
      placeHolder:
        'You’ve reached the maximum number of active logins allowed on your account. To continue here, we’ll need to log you out from your other sessions.',
      onChange: updateSessionLimitSetting,
      value: sessionLimit
    },
    {
      label: 'Log me in here button',
      type: 'textField',
      key: 'continueButton',
      placeHolder: 'Log me in here',
      onChange: updateSessionLimitSetting,
      value: sessionLimit
    },
    {
      label: 'Cancel button',
      type: 'textField',
      key: 'cancelButton',
      placeHolder: 'Cancel',
      onChange: updateSessionLimitSetting,
      value: sessionLimit
    },
    {
      label: 'Success message',
      type: 'textField',
      key: 'successMessage',
      placeHolder: 'You are now successfully logged in.',
      onChange: updateSessionLimitSetting,
      value: sessionLimit
    },
    {
      label: 'Error message',
      type: 'textArea',
      key: 'errorMessage',
      placeHolder: 'We couldn’t log out your other sessions right now. Please try again later.',
      onChange: updateSessionLimitSetting,
      value: sessionLimit
    }
  ];

  const updatedSessionLimitSetting = sessionLimit.enableSessionLimit
    ? [...basicSessionLimitSetting, ...restSessionLimitSetting]
    : basicSessionLimitSetting;

  const renderFieldComponents = (inputArray: IDataItems[]) => {
    return inputArray.map((item: IDataItems) => {
      const { type, label, key, defaultValue, placeHolder, darkKey = '', options, onChange = () => { }, value = {} } =
        item || {};

      switch (type) {
        case 'description':
          return <div className={styles.description}>{label}</div>;
        case 'textArea':
          return (
            <TextArea
              value={get(value, [key], '')}
              onChange={(value: string) => onChange(key, value)}
              hint=""
              label={label}
              placeholder={placeHolder}
            />
          );
        case 'quillEditor':
          const modules = {
            toolbar: [
              ['bold', 'italic', 'underline', 'strike'],
              [{ list: 'ordered' }, { list: 'bullet' }]
            ]
          };
          const formats = ['bold', 'italic', 'underline', 'strike', 'list', 'bullet'];
          return (
            <div className={styles.quillWrapper}>
              <FieldLabel label={label} />
              <ReactQuill
                theme="snow"
                modules={modules}
                formats={formats}
                value={get(value, [key]) || defaultValue}
                onChange={(value: any) => onChange(key, value)}
                placeholder=" "
              />
            </div>
          );
        case 'textField':
          return (
            <TextField
              label={label}
              placeholder={placeHolder}
              value={get(value, [key]) || defaultValue}
              onChange={(value: any) => onChange(key, value)}
            />
          );
        case 'toggle':
          return (
            <div className={styles.toggleContainer}>
              <ToggleButton
                label={label}
                toggleName={key}
                id={key}
                updateToggleState={get(value, [key], defaultValue)}
                updateToggleButton={(name: string, value: boolean) => onChange(name, value)}
              />
            </div>
          );
        case 'colorPicker':
          return (
            <div className={styles.colorPickerWrapper}>
              <ThemeSetter
                label={label}
                updateTheStore={onChange}
                lightColorExtract={get(value, [key], defaultValue)}
                lightUpdateStoreKey={key}
                darkColorExtract={get(value, [darkKey], defaultValue)}
                darkUpdateStoreKey={darkKey}
                lightDefaultColor="#4860BC"
                darkDefaultColor="#4860BC"
                enableDarkModePreview={enableDarkModePreview}
              />
            </div>
          );
        case 'separator':
          return <Separator />;
        case 'select':
          return (
            <div className={styles.selectWrapper}>
              <FieldLabel label={label} />
              <Select
                onChange={(option: { label: string; value: string | boolean; countryIso: string }) => {
                  onChange(key, option.value);
                  onChange('defaultCountryName', option.label);
                  onChange('defaultCountryIso', option.countryIso);
                }}
                id={key}
                options={options}
                defaultValue={options?.find(item => item.label === get(value, ['defaultCountryName'], ''))}
              />
            </div>
          );
        case 'label':
          return (
            <div className={styles.labelWrapper}>
              <FieldLabel label={label} />
            </div>
          );
      }
    });
  };

  return (
    <Accordion hasSingleActivePanel onToggle={() => onClickHandler}>
      <AccordionSection label="Sign in" isLabelUpperCase index={0}>
        {renderFieldComponents(setting)}
      </AccordionSection>
      <AccordionSection label="session limiting" isLabelUpperCase index={1}>
        {renderFieldComponents(updatedSessionLimitSetting)}
      </AccordionSection>
    </Accordion>
  );
};
